import { FormEvent, useEffect, useRef, useState } from 'react'
import Button from '@/components/ui/Button'
import NumberStepper from '@/components/ui/NumberStepper'
import {
  createScheduleSeries,
  previewScheduleSeries,
  ScheduleSeriesPreview,
  ScheduleSeriesRequest,
} from '@/services/schedule'

const DAYS = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье']
const SHORT_DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
const TITLES = ['Когда работаем?', 'Настроим занятия?', 'Когда отдыхаем?']
const INPUT =
  'w-full min-w-0 mt-1 p-3 rounded-lg border border-tg-hint/30 bg-tg-bg text-tg-text text-base'

function errorMessage(error: unknown) {
  const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
  return typeof detail === 'string'
    ? detail
    : 'Не удалось обработать расписание. Проверьте данные и соединение и попробуйте ещё раз.'
}

interface Props {
  defaultDuration: number
  defaultDay: number
  onCancel: () => void
  onCreated: (created: number, skipped: number, replaced: number, days: number[]) => void
}

export default function ScheduleSeriesWizard({
  defaultDuration,
  defaultDay,
  onCancel,
  onCreated,
}: Props) {
  const [step, setStep] = useState(0)
  const [days, setDays] = useState([defaultDay])
  const [start, setStart] = useState('09:00')
  const [end, setEnd] = useState('18:00')
  const [duration, setDuration] = useState(String(defaultDuration))
  const [interval, setInterval] = useState('30')
  const [capacity, setCapacity] = useState(1)
  const [breaks, setBreaks] = useState<ScheduleSeriesRequest['breaks']>([])
  const [preview, setPreview] = useState<ScheduleSeriesPreview | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [conflictStrategy, setConflictStrategy] = useState<'keep' | 'replace'>('keep')
  const heading = useRef<HTMLHeadingElement>(null)
  const isPreviewing = preview !== null

  useEffect(() => {
    heading.current?.focus()
  }, [step, isPreviewing])

  const request = (): ScheduleSeriesRequest => ({
    days,
    start_time: start,
    end_time: end,
    duration_minutes: Number(duration),
    start_interval_minutes: Number(interval),
    max_clients: capacity,
    breaks,
  })

  function validate() {
    if (!days.length) return 'Выберите хотя бы один день недели.'
    if (!start || !end || start >= end) return 'Конец работы должен быть позже начала.'
    if (
      step >= 1 &&
      [Number(duration), Number(interval)].some((n) => !Number.isInteger(n) || n < 1 || n > 1440)
    ) {
      return 'Длительность и шаг начала должны быть целым числом от 1 до 1440 минут.'
    }
    if (
      step >= 2 &&
      breaks.some(
        (b) =>
          !b.start_time ||
          !b.end_time ||
          b.start_time >= b.end_time ||
          b.start_time < start ||
          b.end_time > end
      )
    ) {
      return 'Каждый перерыв должен быть внутри рабочего дня, его конец — позже начала.'
    }
    return ''
  }

  async function next(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    const problem = validate()
    setError(problem)
    if (problem) return
    if (step < 2) {
      setStep(step + 1)
      return
    }
    setBusy(true)
    try {
      setPreview(await previewScheduleSeries(request()))
      setConflictStrategy('keep')
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function save() {
    if (busy) return
    setBusy(true)
    setError('')
    try {
      const result = await createScheduleSeries({
        ...request(),
        conflict_strategy: conflictStrategy,
        expected_revision: preview?.revision,
      })
      onCreated(result.created, result.skipped, result.replaced, days.slice().sort())
    } catch (err) {
      setError(errorMessage(err))
      // Another session may have changed the weekly schedule after preview.
      try {
        setPreview(await previewScheduleSeries(request()))
      } catch {
        /* Keep the last preview and original error. */
      }
    } finally {
      setBusy(false)
    }
  }

  function back() {
    setError('')
    if (preview) setPreview(null)
    else setStep(Math.max(0, step - 1))
  }

  return (
    <section aria-label="Создание расписания недели" className="mb-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <span className="text-sm text-tg-hint">
          {preview ? 'Проверка перед добавлением' : `Шаг ${step + 1} из 3`}
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className="text-tg-button min-h-11 px-2 disabled:opacity-50"
        >
          Отмена
        </button>
      </div>
      {!preview && (
        <div className="flex gap-2 mb-5" aria-hidden="true">
          {TITLES.map((title, i) => (
            <span
              key={title}
              className={`h-1 flex-1 rounded ${i <= step ? 'bg-tg-button' : 'bg-tg-secondary'}`}
            />
          ))}
        </div>
      )}
      <h2 ref={heading} tabIndex={-1} className="text-2xl font-semibold text-tg-text mb-2">
        {preview ? 'Проверим расписание' : TITLES[step]}
      </h2>
      <p className="text-sm text-tg-hint mb-5">
        {preview
          ? 'Интервалы будут повторяться каждую неделю. Слоты на даты создаются отдельно в расписании.'
          : [
              'Выберите дни и границы рабочего времени.',
              'Длительность занятия и шаг между началами могут различаться.',
              'Добавьте обед или паузу. Можно продолжить без перерывов.',
            ][step]}
      </p>

      {!preview && step === 1 && (
        <p className="text-sm text-tg-hint mb-5">
          Например, занятие длится 60 минут, новое занятие начинается каждые 30 минут, тогда будет
          создано расписание: 09:00–10:00, 09:30–10:30. Пересечения между занятиями допустимы.
        </p>
      )}

      {error && (
        <p role="alert" className="text-red-500 bg-tg-secondary rounded-lg p-3 mb-4">
          {error}
        </p>
      )}

      {preview ? (
        <>
          <div className="bg-tg-secondary rounded-xl p-4 text-tg-text mb-4" aria-live="polite">
            <p>
              Добавим: <strong>{preview.create_count}</strong>
            </p>
            <p className="mt-1">
              Уже созданы: <strong>{preview.skip_count}</strong>
            </p>
            {preview.conflict_count > 0 && (
              <fieldset disabled={busy} className="mt-4 space-y-2">
                <legend className="font-medium mb-2">
                  Совпадает время начала: {preview.conflict_count}. Что сделать?
                </legend>
                <label
                  className={`flex items-start gap-3 p-3 min-h-11 rounded-lg border ${conflictStrategy === 'keep' ? 'border-tg-button bg-tg-button/10' : 'border-tg-hint/30'}`}
                >
                  <input
                    type="radio"
                    name="conflict-strategy"
                    value="keep"
                    checked={conflictStrategy === 'keep'}
                    onChange={() => setConflictStrategy('keep')}
                    className="mt-0.5 w-5 h-5 shrink-0 appearance-auto accent-tg-button"
                  />
                  <span>
                    Оставить текущие
                    <span className="block text-sm text-tg-hint">
                      Добавим остальные интервалы. Конфликтующие пропустим.
                    </span>
                  </span>
                </label>
                <label
                  className={`flex items-start gap-3 p-3 min-h-11 rounded-lg border ${conflictStrategy === 'replace' ? 'border-tg-button bg-tg-button/10' : 'border-tg-hint/30'}`}
                >
                  <input
                    type="radio"
                    name="conflict-strategy"
                    value="replace"
                    checked={conflictStrategy === 'replace'}
                    onChange={() => setConflictStrategy('replace')}
                    className="mt-0.5 w-5 h-5 shrink-0 appearance-auto accent-tg-button"
                  />
                  <span>
                    Заменить новыми
                    <span className="block text-sm text-tg-hint">
                      Старые конфликтующие интервалы удалим из недели и добавим рассчитанные.
                    </span>
                  </span>
                </label>
                <p className="text-sm">
                  {conflictStrategy === 'replace' ? 'Заменим' : 'Оставим текущие'}:{' '}
                  {preview.conflict_count}
                </p>
              </fieldset>
            )}
            {preview.create_count === 0 &&
              (conflictStrategy === 'keep' || preview.conflict_count === 0) && (
                <p className="mt-2">
                  Новых интервалов для добавления нет.
                  {preview.conflict_count > 0
                    ? ' Можно выбрать замену конфликтующих.'
                    : ' Всё уже есть в расписании.'}
                </p>
              )}
            {conflictStrategy === 'keep' &&
              preview.intervals.some(
                (i) => i.action === 'conflict' && i.existing.filter((t) => t.is_active).length > 1
              ) && (
                <p className="text-sm mt-2">
                  В текущих интервалах есть дубли. Чтобы убрать их и генерировать слоты, выберите
                  «Заменить новыми».
                </p>
              )}
            <p className="text-sm text-tg-hint mt-2">
              Уже созданные слоты и записи клиентов не изменятся. Остальные интервалы недели
              сохранятся.
            </p>
            {breaks.length > 0 && (
              <p className="text-sm text-tg-hint mt-2">
                Перерывы: {breaks.map((b) => `${b.start_time}–${b.end_time}`).join(', ')}.
                Применяются к новой серии. Остальные интервалы сохраняются.
              </p>
            )}
          </div>
          <div className="space-y-5 mb-5">
            {days
              .slice()
              .sort()
              .map((day) => (
                <div key={day}>
                  <h3 className="font-medium text-tg-text mb-2">{DAYS[day]}</h3>
                  <ul className="space-y-2">
                    {preview.intervals
                      .filter((i) => i.day_of_week === day)
                      .map((item) => (
                        <li
                          key={item.start_time}
                          className="bg-tg-secondary rounded-lg p-3 text-tg-text"
                        >
                          <div className="flex flex-wrap justify-between gap-2">
                            <span>
                              {item.start_time}–{item.end_time}, до {item.max_clients} чел.
                            </span>
                            <span
                              className={
                                item.action === 'conflict' && conflictStrategy === 'replace'
                                  ? 'text-tg-button'
                                  : 'text-tg-hint'
                              }
                            >
                              {item.action === 'create'
                                ? 'Добавим'
                                : item.action === 'skip'
                                  ? 'Уже созданы'
                                  : conflictStrategy === 'replace'
                                    ? 'Заменим'
                                    : 'Оставим текущие'}
                            </span>
                          </div>
                          {item.action === 'conflict' && (
                            <p className="text-sm text-tg-hint mt-1">
                              Сейчас:{' '}
                              {item.existing
                                .map(
                                  (t) =>
                                    `${item.start_time}–${t.end_time}, до ${t.max_clients} чел.${t.is_active ? '' : ', отключён'}`
                                )
                                .join(', ')}
                              {conflictStrategy === 'replace' && (
                                <span className="block text-tg-text mt-1">
                                  Будет: {item.start_time}–{item.end_time}, до {item.max_clients}{' '}
                                  чел., активен
                                </span>
                              )}
                            </p>
                          )}
                          {item.action === 'skip' && !item.existing[0]?.is_active && (
                            <p className="text-sm text-tg-hint mt-1">Останется отключённым</p>
                          )}
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
          </div>
          <Button
            onClick={save}
            loading={busy}
            disabled={
              preview.create_count === 0 &&
              !(conflictStrategy === 'replace' && preview.conflict_count > 0)
            }
            fullWidth
          >
            {conflictStrategy === 'replace' && preview.conflict_count > 0
              ? 'Сохранить с заменой'
              : 'Добавить в расписание'}
          </Button>
          <Button onClick={back} disabled={busy} variant="ghost" fullWidth className="mt-2">
            Изменить настройки
          </Button>
        </>
      ) : (
        <form onSubmit={next} noValidate>
          <fieldset
            disabled={busy}
            className="bg-tg-secondary rounded-xl p-4 space-y-4 mb-5 min-w-0"
          >
            {step === 0 && (
              <>
                <div>
                  <div className="text-sm text-tg-hint mb-2">Дни недели</div>
                  <div className="grid grid-cols-7 gap-1" role="group" aria-label="Дни недели">
                    {SHORT_DAYS.map((label, day) => (
                      <button
                        type="button"
                        key={label}
                        aria-label={DAYS[day]}
                        aria-pressed={days.includes(day)}
                        onClick={() =>
                          setDays(
                            days.includes(day) ? days.filter((d) => d !== day) : [...days, day]
                          )
                        }
                        className={`min-h-11 rounded-lg text-sm ${days.includes(day) ? 'bg-tg-button text-tg-button-text' : 'bg-tg-bg text-tg-text'}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-tg-hint mt-2">
                    Можно выбрать несколько дней с одинаковым расписанием.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-sm text-tg-hint">
                    Начало работы
                    <input
                      className={INPUT}
                      type="time"
                      value={start}
                      onChange={(e) => setStart(e.target.value)}
                    />
                  </label>
                  <label className="text-sm text-tg-hint">
                    Конец работы
                    <input
                      className={INPUT}
                      type="time"
                      value={end}
                      onChange={(e) => setEnd(e.target.value)}
                    />
                  </label>
                </div>
                <p className="text-sm text-tg-hint">
                  Все занятия закончатся до конца вашего рабочего дня.
                </p>
              </>
            )}
            {step === 1 && (
              <>
                <label className="block text-sm text-tg-hint">
                  Длительность занятия, минут
                  <input
                    className={INPUT}
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={1440}
                    step={1}
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                  />
                </label>
                <label className="block text-sm text-tg-hint">
                  Шаг между началами, минут
                  <input
                    className={INPUT}
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={1440}
                    step={1}
                    value={interval}
                    onChange={(e) => setInterval(e.target.value)}
                  />
                </label>
                <div>
                  <div className="text-sm text-tg-hint mb-1">
                    Количество подопечных на одно время
                  </div>
                  <NumberStepper
                    label="Количество подопечных на одно время"
                    value={capacity}
                    onChange={setCapacity}
                    max={2147483647}
                  />
                </div>
              </>
            )}
            {step === 2 && (
              <>
                {breaks.map((pause, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-tg-text">Перерыв {index + 1}</span>
                      <button
                        type="button"
                        aria-label={`Удалить перерыв ${index + 1}`}
                        className="text-tg-button min-h-11 px-2"
                        onClick={() => setBreaks(breaks.filter((_, i) => i !== index))}
                      >
                        Удалить
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {(['start_time', 'end_time'] as const).map((key) => (
                        <label key={key} className="text-sm text-tg-hint">
                          {key === 'start_time' ? 'С' : 'До'}
                          <input
                            type="time"
                            className={INPUT}
                            value={pause[key]}
                            onChange={(e) =>
                              setBreaks(
                                breaks.map((b, i) =>
                                  i === index ? { ...b, [key]: e.target.value } : b
                                )
                              )
                            }
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  disabled={breaks.length >= 24}
                  onClick={() => setBreaks([...breaks, { start_time: '', end_time: '' }])}
                >
                  + Добавить перерыв
                </Button>
                <p className="text-sm text-tg-hint">
                  Новые занятия не будут пересекать эти перерывы. Например, при обеде 13:00–14:00
                  занятие 12:30–13:30 не будет создано.
                </p>
              </>
            )}
          </fieldset>
          <Button type="submit" loading={busy} fullWidth>
            {step === 2 ? 'Посмотреть расписание' : 'Далее'}
          </Button>
          {step > 0 && (
            <Button
              type="button"
              onClick={back}
              disabled={busy}
              variant="ghost"
              fullWidth
              className="mt-2"
            >
              ← Назад
            </Button>
          )}
        </form>
      )}
    </section>
  )
}
